import User from "./User";

/**
 * A class representing a contact with key details such as ID, name, email,
 * and synchronization state.
 */
class CisContact {
    /**
     * The unique identifier of the user
     */
    private readonly id: number;

    /**
     * The last name of the user
     */
    private readonly last_name: string | null;

    /**
     * The first name of the user
     */
    private readonly first_name: string | null;

    /**
     * The email address of the user
     */
    private readonly mail: string | null;

    /**
     * Represents the synchronization state of a given process or entity.
     *
     * This variable acts as a flag to indicate whether the system or component
     * is currently operating in synchronized mode.
     * */
    private readonly ec_sync: boolean


    /**
     * Creates a new instance of the class.
     *
     * @param {number} id - The unique identifier for the instance.
     * @param {string | null} last_name - The last name of the individual. Can be null.
     * @param {string | null} first_name - The first name of the individual. Can be null.
     * @param {string | null} mail - The email address of the individual. Can be null.
     * @param {boolean} ec_sync - Indicates whether the instance is synchronized with EC.
     */
    constructor(
        id: number,
        last_name: string | null,
        first_name: string | null,
        mail: string | null,
        ec_sync: boolean,
    ) {
        this.id = id;
        this.last_name = last_name;
        this.first_name = first_name;
        this.mail = mail;
        this.ec_sync = ec_sync;
    }

    /**
     * Converts a plain JavaScript object into an instance of CisContact.
     *
     * @param {any} item - A JavaScript object with properties corresponding to the CisContact fields.
     * @return {CisContact} The CisContact instance created from the provided object.
     */
    public static fromJson(item: any): CisContact {
        return new CisContact(
            item.id,
            item.nom,
            item.prenom,
            item.email,
            item.ecSync,
        )
    }

    /**
     * Gets the unique identifier of the user.
     *
     * @returns {number} The user's ID.
     */
    public getId(): number {
        return this.id;
    }

    /**
     * Gets the last name of the user.
     *
     * @returns {string | null} The user's last name, or null if not set.
     */
    public getLastName(): string | null {
        return this.last_name;
    }

    /**
     * Gets the first name of the user.
     *
     * @returns {string | null} The user's first name, or null if not set.
     */
    public getFirstName(): string | null {
        return this.first_name;
    }

    /**
     * Gets the email address of the user.
     *
     * @returns {string | null} The user's email address, or null if not set.
     */
    public getMail(): string | null {
        return this.mail;
    }

    /**
     * Gets the synchronization state.
     *
     * @returns {boolean} True if synchronized with EC, false otherwise.
     */
    public getEcSync(): boolean {
        return this.ec_sync;
    }

    /**
     * Converts the current object to an instance of the User class.
     *
     * @return {User} A new instance of the User class with corresponding properties.
     */
    public convertToUser(): User
    {
        return new User(null, this.last_name, this.first_name, this.mail);
    }

}

export default CisContact;